
import React, { useEffect, useState } from "react";

import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Card,
  CardContent,
  Fab,
  CardMedia,
} from "@mui/material";

import SolarPowerIcon from "@mui/icons-material/SolarPower";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import StatsHero from "./StatsHero";
import SolarROILandingFancyParticles from "./ROICalculator";

import AOS from "aos";
import "aos/dist/aos.css";

import { Link } from "react-router-dom";

const MainContent = () => {
  const [showTopBtn, setShowTopBtn] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 800, once: true });

    const handleScroll = () => {
      setShowTopBtn(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box sx={{ overflowX: "hidden", position: "relative", width: "100%" }}>
      {/* ===== HERO ===== */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: "100vh",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: `
          linear-gradient(
          90deg,
          rgba(8, 25, 48, 0.30) 0%,
          rgba(8, 25, 48, 0.18) 45%,
          rgba(8, 25, 48, 0.28) 100%
          ),
            linear-gradient(
              180deg,
              rgba(8, 25, 48, 0.08) 0%,
              rgba(8, 25, 48, 0.18) 100%
            ),
            url("/images/solar-hero.avif")
          `,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
          px: { xs: 2, md: 4 },
          py: { xs: 8, md: 6 },
          textAlign: "center",
          overflow: "hidden",
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ position: "relative", zIndex: 2 }}
          data-aos="fade-down"
        >
          {/* Firm label */}
          <Typography
            component="div"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              mb: 3,
              px: 2,
              py: 1,
              border: "1px solid rgba(242, 211, 92, 0.75)",
              borderRadius: "30px",
              backgroundColor: "rgba(8, 25, 48, 0.35)",
              color: "#f2d35c",
              fontSize: { xs: "0.72rem", md: "0.85rem" },
              fontWeight: 800,
              letterSpacing: "2px",
              textTransform: "uppercase",
            }}
          >
            <SolarPowerIcon sx={{ fontSize: 19 }} />
            "ТИ СИ ЕНЕРДЖИ" ЕООД
          </Typography>

          {/* Main heading */}
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: "#ffffff",
              fontWeight: 800,
              fontSize: {
                xs: "2rem",
                sm: "2.6rem",
                md: "3.4rem",
              },
              lineHeight: 1.18,
              maxWidth: "1000px",
              mx: "auto",
              mb: 3,
              textShadow: "0 3px 18px rgba(0, 0, 0, 0.65)",
            }}
          >
            Професионална грижа за{" "}
            <Box
              component="span"
              sx={{ color: "#f2d35c", display: "inline" }}
            >
              соларни панели и фасади
            </Box>
          </Typography>

          {/* Subtitle */}
          <Typography
            component="p"
            sx={{
              color: "#ffffff",
              fontWeight: 500,
              fontSize: {
                xs: "1.05rem",
                md: "1.3rem",
              },
              lineHeight: 1.8,
              letterSpacing: "0.1px",
              maxWidth: "760px",
              mx: "auto",
              mb: 4,
              textShadow: "0 2px 12px rgba(0, 0, 0, 0.85)",
            }}
          >
            Високоефективни препарати за чисти панели и надеждна поддръжка.
          </Typography>

          {/* Call-to-action buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              component={Link}
              to="/preparati-za-solarni-paneli"
              onClick={scrollToTop}
              variant="contained"
              size="large"
              sx={{
                backgroundColor: "#f2d35c",
                color: "#203858",
                fontWeight: 800,
                px: 3.5,
                py: 1.6,
                borderRadius: "8px",
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.22)",
                "&:hover": {
                  backgroundColor: "#ffdf70",
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 28px rgba(0, 0, 0, 0.3)",
                },
                transition: "all 0.2s ease",
              }}
            >
              Виж продуктите
            </Button>

            <Button
              component={Link}
              to="/oferta-na-preparati-za-solarni-paneli"
              onClick={scrollToTop}
              variant="outlined"
              size="large"
              sx={{
                color: "#ffffff",
                borderColor: "rgba(255, 255, 255, 0.9)",
                borderWidth: "1.5px",
                fontWeight: 700,
                px: 3.5,
                py: 1.6,
                borderRadius: "8px",
                backgroundColor: "rgba(8, 25, 48, 0.25)",
                "&:hover": {
                  borderColor: "#f2d35c",
                  color: "#f2d35c",
                  borderWidth: "1.5px",
                  backgroundColor: "rgba(8, 25, 48, 0.6)",
                },
                transition: "all 0.2s ease",
              }}
            >
              Заяви оферта
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ===== STATS ===== */}
      <Box data-aos="fade-up">
        <StatsHero />
      </Box>

      {/* ===== ROI CALCULATOR ===== */}
      <Box data-aos="fade-up">
        <SolarROILandingFancyParticles />
      </Box>

      {/* ===== TESTIMONIALS - currently disabled ===== */}
      {/*
      <Box data-aos="fade-up">
        <TestimonialsCarousel />
      </Box>
      */}

      {/* ===== PRODUCTS AFTER CALCULATOR ===== */}
      <Box
        component="section"
        aria-labelledby="featured-products-title"
        sx={{
          position: "relative",
          overflow: "hidden",
          py: { xs: 7, md: 11 },
          background: "linear-gradient(180deg, #f7f9fc 0%, #eaf0f8 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 780, mx: "auto", textAlign: "center", mb: { xs: 4, md: 6 } }} data-aos="fade-up">
            <Typography
              component="div"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 2,
                py: 0.8,
                mb: 2,
                borderRadius: 99,
                bgcolor: "rgba(242, 211, 92, 0.24)",
                color: "#2f4f7f",
                fontSize: "0.78rem",
                fontWeight: 800,
                letterSpacing: "1.2px",
                textTransform: "uppercase",
              }}
            >
              Решения за поддръжка
            </Typography>
            <Typography
              id="featured-products-title"
              variant="h4"
              component="h2"
              sx={{ color: "#203858", fontWeight: 800, mb: 2, fontSize: { xs: "1.85rem", md: "2.5rem" } }}
            >
              Подходящият препарат започва с правилната грижа
            </Typography>
            <Typography sx={{ color: "#5c6b7f", fontSize: { xs: "1rem", md: "1.1rem" }, lineHeight: 1.8 }}>
              Разгледайте решенията ни за почистване и поддръжка на соларни панели. Изберете продукт според нуждите на вашата система или поискайте индивидуална консултация.
            </Typography>
          </Box>

          <Grid container spacing={3} alignItems="stretch">
            {[
              {
                title: "Solar Wash Protect (SWP)",
                subtitle: "Почистване и защитен ефект",
                text: "За замърсявания като прах, полени, сажди и птичи екскременти. Формулата е предназначена за почистване и създаване на антистатичен и водоотблъскващ филм.",
                image: "/images/agents/swp.avif",
                tag: "Почистване 1:50",
              },
              {
                title: "Antistatic Solar Agent (ASA)",
                subtitle: "Поддръжка между почистванията",
                text: "Решение за антистатична и водоотблъскваща обработка на соларни панели. Следвайте указанията на производителя за разреждане и приложение.",
                image: "/images/agents/asa.avif",
                tag: "Концентрат 1:1000",
              },
            ].map((item, index) => (
              <Grid item xs={12} md={6} key={item.title} data-aos="fade-up" data-aos-delay={index * 100}>
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    height: "100%",
                    overflow: "hidden",
                    borderRadius: 4,
                    border: "1px solid rgba(47, 79, 127, 0.12)",
                    boxShadow: "0 12px 36px rgba(32, 56, 88, 0.08)",
                    transition: "transform .25s ease, box-shadow .25s ease",
                    "&:hover": { transform: "translateY(-5px)", boxShadow: "0 18px 42px rgba(32, 56, 88, 0.15)" },
                  }}
                >
                  <Box
                    sx={{
                      flex: { sm: "0 0 38%" },
                      minHeight: { xs: 210, sm: 280 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 3,
                      background: index === 0
                        ? "linear-gradient(145deg, #e8f5e9, #ffffff)"
                        : "linear-gradient(145deg, #e3f2fd, #ffffff)",
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={item.image}
                      alt={item.title}
                      loading="lazy"
                      sx={{ width: "100%", maxWidth: 180, height: 210, objectFit: "contain" }}
                    />
                  </Box>
                  <CardContent sx={{ p: { xs: 3, md: 3.5 }, display: "flex", flexDirection: "column", flex: 1 }}>
                    <Typography
                      component="span"
                      sx={{ alignSelf: "flex-start", mb: 1.5, px: 1.3, py: 0.6, borderRadius: 99, bgcolor: "#fff5cf", color: "#725a12", fontSize: "0.75rem", fontWeight: 800 }}
                    >
                      {item.tag}
                    </Typography>
                    <Typography variant="h5" component="h3" sx={{ color: "#203858", fontWeight: 800, mb: 1 }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: "#2f4f7f", fontWeight: 700, mb: 1.5 }}>
                      {item.subtitle}
                    </Typography>
                    <Typography sx={{ color: "#5c6b7f", lineHeight: 1.75, mb: 3 }}>
                      {item.text}
                    </Typography>
                    <Box sx={{ mt: "auto" }}>
                      <Button
                        component={Link}
                        to="/preparati-za-solarni-paneli"
                        onClick={scrollToTop}
                        variant="contained"
                        fullWidth
                        sx={{
                          py: 1.3,
                          borderRadius: 2,
                          bgcolor: "#2f4f7f",
                          color: "#fff",
                          fontWeight: 800,
                          textTransform: "none",
                          "&:hover": { bgcolor: "#203858" },
                        }}
                      >
                        Разгледай продукта
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: { xs: 4, md: 5 }, p: { xs: 3, md: 3.5 }, borderRadius: 3, bgcolor: "#203858", color: "#fff", display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "stretch", md: "center" }, justifyContent: "space-between", gap: 2 }} data-aos="fade-up">
            <Box>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 800, mb: 0.5 }}>
                Не сте сигурни кой продукт е подходящ?
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.7 }}>
                Изпратете запитване и ще уточним нуждите на вашата соларна система.
              </Typography>
            </Box>
            <Button
              component={Link}
              to="/oferta-na-preparati-za-solarni-paneli"
              onClick={scrollToTop}
              variant="contained"
              sx={{ flexShrink: 0, py: 1.4, px: 3, bgcolor: "#f2d35c", color: "#203858", fontWeight: 800, borderRadius: 2, textTransform: "none", "&:hover": { bgcolor: "#ffdf70" } }}
            >
              Поискай оферта
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ===== COMPLETED PROJECTS - currently disabled ===== */}
      {/*
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h2"
            fontWeight="bold"
            textAlign="center"
            mb={5}
            sx={{ color: "#2f4f7f" }}
            data-aos="fade-up"
          >
            Реализирани проекти
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6} data-aos="fade-right">
              <Box
                component="img"
                src="/images/project-before.jpg"
                alt="Преди"
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  mb: 1,
                  objectFit: "cover",
                }}
              />
              <Typography>Преди</Typography>
            </Grid>

            <Grid item xs={12} md={6} data-aos="fade-left">
              <Box
                component="img"
                src="/images/project-after.jpg"
                alt="След"
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  mb: 1,
                  objectFit: "cover",
                }}
              />
              <Typography>След</Typography>
            </Grid>
          </Grid>

          <Typography mt={3} data-aos="fade-up">
            Почистихме 10 000 м² соларни панели. Ефективност +15%
          </Typography>
        </Container>
      </Box>
      */}

      {/* ===== FACADE CLEANING SERVICE ===== */}
      <Box
        component="section"
        aria-labelledby="facade-service-title"
        sx={{
          position: "relative",
          overflow: "hidden",
          py: { xs: 7, md: 10 },
          px: { xs: 2, sm: 3 },
          background:
            "linear-gradient(135deg, #102846 0%, #173b67 58%, #204c7d 100%)",
          color: "#ffffff",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={6} data-aos="fade-right">
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.8,
                  py: 0.8,
                  mb: 2.5,
                  border: "1px solid rgba(242, 211, 92, 0.55)",
                  borderRadius: 99,
                  backgroundColor: "rgba(242, 211, 92, 0.1)",
                  color: "#f2d35c",
                  fontSize: "0.78rem",
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                Допълнителна услуга чрез партньори
              </Box>

              <Typography
                id="facade-service-title"
                component="h2"
                sx={{
                  color: "#ffffff",
                  fontWeight: 800,
                  fontSize: { xs: "1.9rem", md: "2.65rem" },
                  lineHeight: 1.2,
                  mb: 2.5,
                }}
              >
                Почистване на фасади с дронове
              </Typography>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.82)",
                  fontSize: { xs: "1rem", md: "1.08rem" },
                  lineHeight: 1.85,
                  mb: 2,
                }}
              >
                Съдействаме за организиране на професионално почистване на
                фасади и труднодостъпни външни повърхности чрез специализирани
                партньори.
              </Typography>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.72)",
                  lineHeight: 1.8,
                  mb: 3.5,
                }}
              >
                Изпратете запитване с информация за обекта. Ще обсъдим
                изискванията и ще уточним възможностите за изпълнение.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  alignItems: { xs: "stretch", sm: "center" },
                }}
              >
                <Button
                  component={Link}
                  to="/oferta-na-preparati-za-solarni-paneli"
                  onClick={scrollToTop}
                  variant="contained"
                  size="large"
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    backgroundColor: "#f2d35c",
                    color: "#203858",
                    fontWeight: 800,
                    textTransform: "none",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                    "&:hover": {
                      backgroundColor: "#ffdf70",
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 28px rgba(0,0,0,0.25)",
                    },
                    transition: "all 180ms ease",
                  }}
                >
                  Поискай оферта за услугата
                </Button>

                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.65)",
                    lineHeight: 1.6,
                  }}
                >
                  Офертата се уточнява според конкретния обект.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6} data-aos="fade-left">
              <Box
                sx={{
                  position: "relative",
                  borderRadius: { xs: 3, md: 4 },
                  overflow: "hidden",
                  minHeight: { xs: 260, sm: 340, md: 410 },
                  background:
                    "linear-gradient(145deg, rgba(255,255,255,0.10), rgba(255,255,255,0.02))",
                  border: "1px solid rgba(255,255,255,0.16)",
                  boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: { xs: 2, md: 3 },
                }}
              >
                <Box
                  component="img"
                  src="/images/drone-facade-cleaning.png"
                  alt="Почистване на фасада с дрон"
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(180deg, rgba(16,40,70,0.02) 20%, rgba(16,40,70,0.72) 100%)",
                    pointerEvents: "none",
                  }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    left: { xs: 3, md: 4 },
                    right: { xs: 3, md: 4 },
                    bottom: { xs: 3, md: 4 },
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "rgba(16,40,70,0.76)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  <Typography
                    component="p"
                    sx={{ fontWeight: 800, color: "#ffffff", mb: 0.5 }}
                  >
                    Решение според вашия обект
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.65 }}
                  >
                    Възможностите и условията се потвърждават след уточняване
                    на обекта и обхвата на работата.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

            {/* ===== SCROLL TO TOP ===== */}
      {showTopBtn && (
        <Fab
          color="primary"
          onClick={scrollToTop}
          aria-label="Scroll to top"
          sx={{
            position: "fixed",
            bottom: 40,
            right: 30,
            backgroundColor: "#2f4f7f",
            color: "#f2d35c",
            zIndex: 1200,
            "&:hover": {
              backgroundColor: "#1f3558",
              transform: "scale(1.1)",
            },
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      )}
    </Box>
  );
};

export default MainContent;
